import { ImageZoom } from '../components/ImageZoom';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cbseAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Skeleton } from '../components/ui/skeleton';
import { SUBJECT_COLORS, cn } from '../lib/utils';
import { ExplanationRenderer } from '../components/core/ExplanationRenderer';
import { ArrowLeft, CheckCircle, XCircle, BookOpen } from 'lucide-react';
import Layout from '../components/Layout';

export default function CBSEReview() {
    const { examId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [reviewData, setReviewData] = useState(null);

    useEffect(() => {
        fetchReview();
    }, [examId]);

    const fetchReview = async () => {
        try {
            setLoading(true);
            const response = await cbseAPI.reviewExam(examId);
            setReviewData(response.data);
        } catch (err) {
            console.error('Failed to fetch review:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-32" />
                    <Skeleton className="h-48" />
                </div>
            </Layout>
        );
    }

    if (!reviewData) {
        return (
            <Layout>
                <Card className="border-slate-200">
                    <CardContent className="p-12 text-center">
                        <p className="text-slate-500">Exam not found</p>
                        <Button onClick={() => navigate('/cbse')} className="mt-4">
                            Back to CBSE
                        </Button>
                    </CardContent>
                </Card>
            </Layout>
        );
    }

    const { result, questions } = reviewData;

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="cbse-review-container">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/cbse')}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Exam Review
                        </h1>
                        <p className="text-slate-500">
                            Score: {result?.score_percentage}% • {result?.correct_answers}/{result?.total_questions} correct
                        </p>
                    </div>
                </div>

                {/* Questions */}
                <Accordion type="single" collapsible className="space-y-3">
                    {questions.map((item, idx) => {
                        const { question, user_answer, is_correct } = item;
                        const colors = SUBJECT_COLORS[question.subject] || {};

                        return (
                            <AccordionItem
                                key={idx}
                                value={`q-${idx}`}
                                className="border border-slate-200 rounded-xl overflow-hidden bg-white"
                            >
                                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                                    <div className="flex items-center gap-3 text-left">
                                        <div className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                                            is_correct ? "bg-emerald-100" : "bg-red-100"
                                        )}>
                                            {is_correct ? (
                                                <CheckCircle className="h-5 w-5 text-emerald-600" />
                                            ) : (
                                                <XCircle className="h-5 w-5 text-red-600" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-medium text-slate-500">Q{idx + 1}</span>
                                                <Badge className={cn(colors.bg, colors.text, "text-xs")}>
                                                    {question.subject}
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-slate-700 line-clamp-1">
                                                {question.question_stem}
                                            </p>
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-4 pb-4">
                                    <div className="space-y-4 pt-2 border-t border-slate-100">
                                        {/* Medical Image with description (shown in review) */}
                                        {question.image_base64 && (
                                            <div>
                                                <div className="rounded-lg overflow-hidden border-2 border-slate-300 bg-white">
                                                    <ImageZoom 
                                                        src={`data:image/png;base64,${question.image_base64}`}
                                                        alt="Medical image"
                                                    />
                                                </div>
                                                {question.image_description && (
                                                    <div className="mt-2 p-2 rounded bg-indigo-50 border border-indigo-200">
                                                        <p className="text-xs text-indigo-700">
                                                            <span className="font-medium">Image:</span> {question.image_description}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Vignette */}
                                        <div className="p-3 rounded-lg bg-slate-50 text-sm text-slate-600">
                                            {question.vignette}
                                        </div>

                                        {/* Question */}
                                        <p className="font-medium text-slate-900">{question.question_stem}</p>

                                        {/* Options */}
                                        <div className="space-y-2">
                                            {question.options.map((option) => {
                                                const isCorrectAnswer = option.letter === question.correct_answer;
                                                const wasSelected = option.letter === user_answer;

                                                return (
                                                    <div
                                                        key={option.letter}
                                                        className={cn(
                                                            "flex items-start gap-3 p-3 rounded-lg border-2",
                                                            isCorrectAnswer && "border-emerald-300 bg-emerald-50",
                                                            wasSelected && !isCorrectAnswer && "border-red-300 bg-red-50",
                                                            !isCorrectAnswer && !wasSelected && "border-slate-200"
                                                        )}
                                                    >
                                                        <div className={cn(
                                                            "w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0",
                                                            isCorrectAnswer && "bg-emerald-600 text-white",
                                                            wasSelected && !isCorrectAnswer && "bg-red-600 text-white",
                                                            !isCorrectAnswer && !wasSelected && "bg-slate-200 text-slate-600"
                                                        )}>
                                                            {isCorrectAnswer ? (
                                                                <CheckCircle className="h-4 w-4" />
                                                            ) : wasSelected ? (
                                                                <XCircle className="h-4 w-4" />
                                                            ) : (
                                                                option.letter
                                                            )}
                                                        </div>
                                                        <span className={cn(
                                                            "text-sm",
                                                            isCorrectAnswer && "text-emerald-800 font-medium",
                                                            wasSelected && !isCorrectAnswer && "text-red-800"
                                                        )}>
                                                            {option.text}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Explanation */}
                                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                            <div className="flex items-center gap-2 mb-2">
                                                <BookOpen className="h-4 w-4 text-amber-600" />
                                                <span className="font-medium text-amber-800 text-sm">Explanation</span>
                                            </div>
                                            <ExplanationRenderer text={question.explanation} className="text-sm text-amber-900" />
                                        </div>

                                        {!user_answer && (
                                            <p className="text-sm text-slate-500 italic">This question was not answered.</p>
                                        )}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>
        </Layout>
    );
}
